import {
  deleteAdminTournament,
  fetchAdminTournamentTemplates,
  fetchAdminTournaments,
  patchAdminTournament,
  postAdminTournament,
} from "../lib/cloud";
import {
  TOURNAMENT_BRACKET_SIZES,
  TOURNAMENT_RECURRENCES,
  parseRecurrenceInterval,
  tournamentMatchCount,
  type TournamentMatchModeConfig,
  type TournamentRoundRewards,
} from "./tournaments/types";
import { renderAdminBracketPreview } from "./tournaments/bracket-ui";

function escapeHtml(s: string) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function defaultMatchModes(size: number): TournamentMatchModeConfig[] {
  const count = tournamentMatchCount(size);
  return Array.from({ length: count }, (_, i) => ({ matchIndex: i, modeId: "normal" }));
}

function toDatetimeLocalValue(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatStarts(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return String(iso).slice(0, 16);
  return d.toLocaleString();
}

type AdminTournamentRow = {
  id: string;
  name: string;
  status: string;
  startsAt: string;
  originalStartsAt?: string;
  bracketSize: number;
  registeredCount: number;
  matchModes?: TournamentMatchModeConfig[];
  roundRewards?: TournamentRoundRewards;
};

export function mountAdminTournaments(root: HTMLElement) {
  root.replaceChildren();
  let bracketSize = 8;
  let matchModes = defaultMatchModes(bracketSize);
  let roundRewards: TournamentRoundRewards = { "1": 0, win: 0 };
  let editingId: string | null = null;

  const toolbar = document.createElement("div");
  toolbar.className = "modeBox adminRecordsFilters";
  const createBtn = document.createElement("button");
  createBtn.type = "button";
  createBtn.className = "btn green";
  createBtn.textContent = "Create Tournament";
  const refreshBtn = document.createElement("button");
  refreshBtn.type = "button";
  refreshBtn.className = "btn";
  refreshBtn.textContent = "Refresh";
  const templateBtn = document.createElement("button");
  templateBtn.type = "button";
  templateBtn.className = "btn";
  templateBtn.textContent = "Create Recurring Template";
  toolbar.append(createBtn, templateBtn, refreshBtn);
  root.appendChild(toolbar);

  const status = document.createElement("p");
  status.className = "accessHint";
  root.appendChild(status);

  const editor = document.createElement("div");
  editor.className = "modeBox adminAchievementEditor hidden";
  root.appendChild(editor);

  const listHeading = document.createElement("h4");
  listHeading.className = "adminPlayerStatsSubheading";
  listHeading.textContent = "Tournaments";
  root.appendChild(listHeading);

  const listWrap = document.createElement("div");
  listWrap.className = "adminTournamentsList";
  root.appendChild(listWrap);

  const templatesHeading = document.createElement("h4");
  templatesHeading.className = "adminPlayerStatsSubheading";
  templatesHeading.textContent = "Recurring templates";
  root.appendChild(templatesHeading);
  const templatesWrap = document.createElement("div");
  templatesWrap.className = "adminTournamentsTemplates";
  root.appendChild(templatesWrap);

  let previewMount: HTMLElement | null = null;

  function rebuildPreview() {
    if (!previewMount) return;
    previewMount.replaceChildren(
      renderAdminBracketPreview(bracketSize, matchModes, (idx, modeId) => {
        matchModes[idx] = { matchIndex: idx, modeId };
      }),
    );
  }

  function makeActionBtn(label: string, kind: "default" | "green" | "danger" = "default") {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className =
      kind === "green" ? "btn green adminTournamentActionBtn" : "btn adminTournamentActionBtn";
    if (kind === "danger") btn.classList.add("adminTournamentActionBtn--danger");
    btn.textContent = label;
    return btn;
  }

  function openEditor(
    isTemplate = false,
    existing?: AdminTournamentRow,
  ) {
    editingId = existing?.id ?? null;
    editor.classList.remove("hidden");
    editor.replaceChildren();
    editor.scrollIntoView({ behavior: "smooth", block: "nearest" });

    if (existing) {
      bracketSize = Number(existing.bracketSize) || 8;
      matchModes = existing.matchModes?.length
        ? existing.matchModes.map((m) => ({ ...m }))
        : defaultMatchModes(bracketSize);
      roundRewards = { ...(existing.roundRewards ?? { "1": 0, win: 0 }) };
    }

    const title = document.createElement("h3");
    title.textContent = isTemplate
      ? "New recurring template"
      : existing
        ? "Edit tournament"
        : "New tournament";
    const nameInput = document.createElement("input");
    nameInput.className = "formInput";
    nameInput.placeholder = "Tournament name";
    nameInput.value = existing?.name ?? "";
    const sizeSel = document.createElement("select");
    sizeSel.className = "formInput";
    for (const s of TOURNAMENT_BRACKET_SIZES) {
      const opt = document.createElement("option");
      opt.value = String(s);
      opt.textContent = `${s} players`;
      if (s === bracketSize) opt.selected = true;
      sizeSel.appendChild(opt);
    }
    sizeSel.disabled = !!existing;
    const startInput = document.createElement("input");
    startInput.type = "datetime-local";
    startInput.className = "formInput";
    if (existing?.startsAt) startInput.value = toDatetimeLocalValue(existing.startsAt);
    const recurSel = document.createElement("select");
    recurSel.className = "formInput";
    recurSel.innerHTML = '<option value="">One-time</option>';
    for (const r of TOURNAMENT_RECURRENCES) {
      const opt = document.createElement("option");
      opt.value = r;
      opt.textContent = r === "custom" ? "Custom" : r.charAt(0).toUpperCase() + r.slice(1);
      recurSel.appendChild(opt);
    }
    const recurIntervalInput = document.createElement("input");
    recurIntervalInput.type = "text";
    recurIntervalInput.className = "formInput hidden";
    recurIntervalInput.placeholder = 'Custom interval e.g. "1d, 5h, 15m, 15s" or "30m"';
    recurIntervalInput.title = 'Examples: 30m · 1d · 1 dia · 1h · 1 hora · 1d, 5h, 15m, 15s';
    const recurIntervalHint = document.createElement("p");
    recurIntervalHint.className = "accessHint hidden";
    recurIntervalHint.textContent =
      'Custom interval: use d/dia, h/hora, m/min, s/seg. Comma-separated. Omitted units count as 0 (e.g. "30m").';
    function syncRecurrenceFields() {
      const custom = recurSel.value === "custom";
      recurIntervalInput.classList.toggle("hidden", !custom);
      recurIntervalHint.classList.toggle("hidden", !custom);
    }
    recurSel.onchange = () => syncRecurrenceFields();
    if (!isTemplate) recurSel.disabled = true;

    const rewardsHint = document.createElement("p");
    rewardsHint.className = "accessHint";
    rewardsHint.textContent =
      "Round Dots go to that round's winners. On the Final, Round Dots go to the runner-up and Win Dots go to the champion.";

    const rewardsRow = document.createElement("div");
    rewardsRow.className = "rgbRow adminTournamentRewardsRow";
    const rewardInputs = new Map<string, HTMLInputElement>();

    function rebuildRewardInputs() {
      rewardsRow.replaceChildren();
      rewardInputs.clear();
      const totalRounds = Math.log2(bracketSize);
      for (let r = 1; r <= totalRounds; r++) {
        const inp = document.createElement("input");
        inp.type = "number";
        inp.min = "0";
        inp.className = "formInput";
        const isFinalRound = r === totalRounds;
        inp.placeholder = isFinalRound ? `Final (2nd place) Dots` : `Round ${r} Dots`;
        inp.title = isFinalRound
          ? "Awarded to the runner-up of the final"
          : `Awarded to winners of round ${r}`;
        inp.value = String(roundRewards[String(r)] ?? 0);
        rewardInputs.set(String(r), inp);
        rewardsRow.appendChild(inp);
      }
      const winInp = document.createElement("input");
      winInp.type = "number";
      winInp.min = "0";
      winInp.className = "formInput";
      winInp.placeholder = "Champion (Win) Dots";
      winInp.title = "Awarded to the tournament champion";
      winInp.value = String(roundRewards.win ?? 0);
      rewardInputs.set("win", winInp);
      rewardsRow.appendChild(winInp);
    }
    rebuildRewardInputs();

    previewMount = document.createElement("div");
    previewMount.className = "tournamentAdminPreview";
    rebuildPreview();

    const saveBtn = makeActionBtn(existing ? "Save changes" : "Save", "green");
    const cancelBtn = makeActionBtn("Close");
    cancelBtn.onclick = () => {
      editingId = null;
      editor.classList.add("hidden");
    };

    sizeSel.onchange = () => {
      bracketSize = Number(sizeSel.value);
      matchModes = defaultMatchModes(bracketSize);
      rebuildPreview();
      rebuildRewardInputs();
    };

    saveBtn.onclick = async () => {
      const name = nameInput.value.trim();
      if (!name) {
        status.textContent = "Name required.";
        return;
      }
      const nextRewards: TournamentRoundRewards = {};
      for (const [key, inp] of rewardInputs) {
        nextRewards[key] = Math.max(0, Math.floor(Number(inp.value) || 0));
      }
      roundRewards = nextRewards;
      const startsAt = startInput.value ? new Date(startInput.value).toISOString() : undefined;
      const recurrence = isTemplate ? recurSel.value || null : null;
      let recurrenceInterval: string | undefined;
      if (isTemplate && recurrence === "custom") {
        recurrenceInterval = recurIntervalInput.value.trim();
        if (!parseRecurrenceInterval(recurrenceInterval)) {
          status.textContent =
            'Invalid custom interval. Examples: "30m", "1d", "1 dia", "1h", "1d, 5h, 15m, 15s".';
          return;
        }
      }
      try {
        if (existing) {
          await patchAdminTournament({
            id: existing.id,
            name,
            matchModes,
            roundRewards,
            startsAt,
          });
        } else if (isTemplate) {
          await postAdminTournament({
            section: "template",
            name,
            bracketSize,
            matchModes,
            roundRewards,
            startsAt,
            recurrence,
            recurrenceInterval,
          });
        } else {
          await postAdminTournament({
            name,
            bracketSize,
            matchModes,
            roundRewards,
            startsAt,
          });
        }
        editingId = null;
        editor.classList.add("hidden");
        status.textContent = "Saved.";
        await refreshLists();
      } catch (e) {
        status.textContent = e instanceof Error ? e.message : "Save failed.";
      }
    };

    editor.append(
      title,
      nameInput,
      sizeSel,
      startInput,
      ...(isTemplate ? [recurSel, recurIntervalInput, recurIntervalHint] : []),
      rewardsHint,
      rewardsRow,
      previewMount,
      saveBtn,
      cancelBtn,
    );
    syncRecurrenceFields();
  }

  function renderTournamentCard(t: AdminTournamentRow): HTMLElement {
    const card = document.createElement("article");
    card.className = "adminTournamentCard";
    card.dataset.status = t.status;

    const info = document.createElement("div");
    info.className = "adminTournamentCardInfo";
    const name = document.createElement("p");
    name.className = "adminTournamentCardName";
    name.textContent = t.name;
    const meta = document.createElement("p");
    meta.className = "adminTournamentCardMeta";
    meta.textContent = `${t.status.toUpperCase()} · ${t.registeredCount}/${t.bracketSize} players · starts ${formatStarts(t.startsAt)}`;
    info.append(name, meta);

    const actions = document.createElement("div");
    actions.className = "adminTournamentActions";

    const canEdit = t.status === "upcoming" || t.status === "open";
    const canStart = t.status === "upcoming" || t.status === "open";
    const canCancel = t.status === "upcoming" || t.status === "open" || t.status === "active";
    const canDelete = t.status === "upcoming" || t.status === "open";

    if (canStart) {
      const startBtn = makeActionBtn("Start", "green");
      startBtn.title = "Open registration now, or begin bracket if full";
      startBtn.onclick = async () => {
        try {
          await patchAdminTournament({ id: t.id, action: "start" });
          status.textContent = "Tournament started.";
          await refreshLists();
        } catch (e) {
          status.textContent = e instanceof Error ? e.message : "Start failed.";
        }
      };
      actions.appendChild(startBtn);
    }

    if (canEdit) {
      const editBtn = makeActionBtn("Edit");
      editBtn.onclick = () => openEditor(false, t);
      actions.appendChild(editBtn);

      const rescheduleBtn = makeActionBtn("Set start");
      rescheduleBtn.onclick = async () => {
        const next = window.prompt(
          "New start time (YYYY-MM-DDTHH:MM)",
          toDatetimeLocalValue(t.startsAt),
        );
        if (!next) return;
        const d = new Date(next);
        if (!Number.isFinite(d.getTime())) {
          status.textContent = "Invalid date.";
          return;
        }
        try {
          await patchAdminTournament({ id: t.id, startsAt: d.toISOString() });
          status.textContent = "Start time updated.";
          await refreshLists();
        } catch (e) {
          status.textContent = e instanceof Error ? e.message : "Update failed.";
        }
      };
      actions.appendChild(rescheduleBtn);
    }

    if (canCancel) {
      const cancelBtn = makeActionBtn("Cancel");
      cancelBtn.title = "Reset to upcoming (clears matches). Keeps a future start time.";
      cancelBtn.onclick = async () => {
        const ok = window.confirm(
          `Cancel "${t.name}"?\n\nThis clears the bracket/matches and sets status back to upcoming.`,
        );
        if (!ok) return;
        cancelBtn.disabled = true;
        status.textContent = "Canceling…";
        try {
          const result = await patchAdminTournament({ id: t.id, action: "cancel" });
          const nextStart =
            typeof result.startsAt === "string" ? formatStarts(result.startsAt) : "";
          await refreshLists();
          status.textContent = nextStart
            ? `Canceled. Status: upcoming. Start: ${nextStart}`
            : "Canceled. Status: upcoming.";
        } catch (e) {
          const msg = e instanceof Error ? e.message : "Cancel failed.";
          status.textContent = msg;
          window.alert(`Cancel failed: ${msg}`);
        } finally {
          cancelBtn.disabled = false;
        }
      };
      actions.appendChild(cancelBtn);
    }

    if (canDelete) {
      const del = makeActionBtn("Delete", "danger");
      del.onclick = async () => {
        if (!window.confirm(`Delete tournament “${t.name}”?`)) return;
        try {
          await deleteAdminTournament(t.id);
          status.textContent = "Deleted.";
          await refreshLists();
        } catch (e) {
          status.textContent = e instanceof Error ? e.message : "Delete failed.";
        }
      };
      actions.appendChild(del);
    }

    if (!actions.childNodes.length) {
      const none = document.createElement("span");
      none.className = "adminTournamentNoActions";
      none.textContent = "No actions";
      actions.appendChild(none);
    }

    card.append(info, actions);
    return card;
  }

  async function refreshLists() {
    status.textContent = "Loading…";
    try {
      const [inst, tpl] = await Promise.all([
        fetchAdminTournaments(),
        fetchAdminTournamentTemplates(),
      ]);
      const tournaments = ((inst.tournaments as AdminTournamentRow[]) ?? []).map((t) => ({
        ...t,
        id: String(t.id),
        name: String(t.name ?? ""),
        status: String(t.status ?? ""),
        startsAt: String(t.startsAt ?? ""),
        bracketSize: Number(t.bracketSize) || 0,
        registeredCount: Number(t.registeredCount) || 0,
      }));
      listWrap.replaceChildren();
      if (!tournaments.length) {
        const empty = document.createElement("p");
        empty.className = "accessHint";
        empty.textContent = "No tournaments yet. Create one above.";
        listWrap.appendChild(empty);
      } else {
        for (const t of tournaments) {
          listWrap.appendChild(renderTournamentCard(t));
        }
      }

      const templates = (tpl.templates as Array<Record<string, unknown>>) ?? [];
      templatesWrap.replaceChildren();
      if (!templates.length) {
        const empty = document.createElement("p");
        empty.className = "accessHint";
        empty.textContent = "No recurring templates.";
        templatesWrap.appendChild(empty);
      } else {
        for (const t of templates) {
          const row = document.createElement("div");
          row.className = "adminTournamentTemplateRow";
          const recurrenceLabel =
            t.recurrence === "custom" && t.recurrenceInterval
              ? `custom (${String(t.recurrenceInterval)})`
              : (t.recurrence ?? "one-time");
          row.textContent = `${t.name} — ${recurrenceLabel} — ${t.bracketSize}p`;
          templatesWrap.appendChild(row);
        }
      }
      if (status.textContent === "Loading…") status.textContent = `${tournaments.length} tournament(s)`;
    } catch (e) {
      status.textContent = e instanceof Error ? e.message : "Load failed.";
    }
  }

  createBtn.onclick = () => {
    editingId = null;
    bracketSize = 8;
    matchModes = defaultMatchModes(bracketSize);
    roundRewards = { "1": 0, win: 0 };
    openEditor(false);
  };
  templateBtn.onclick = () => {
    editingId = null;
    bracketSize = 8;
    matchModes = defaultMatchModes(bracketSize);
    roundRewards = { "1": 0, win: 0 };
    openEditor(true);
  };
  refreshBtn.onclick = () => void refreshLists();
  void refreshLists();
}
